import { useAuth } from '@pooflabs/web';
import { Coins, Loader2, Sparkles, Wallet, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AgentCapabilityPicker } from '@/components/AgentCapabilityPicker';
import { BuyCreditsDialog, useCreditBalance } from '@/components/CreditBalance';
import TopNav from '@/components/TopNav';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { PageLayout, PageHeader } from '@/components/poof-ui';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { setAgentCreations } from '@/lib/collections/agentCreations';
import { getAppFiles, uploadAppFiles } from '@/lib/collections/appFiles';
import { setAgents, subscribeAgents, updateAgents, type AgentsResponse } from '@/lib/collections/agents';
import { AGENT_BASE_COST, AGENT_PER_CAPABILITY_COST } from '@/lib/constants';
import { Address, Time } from '@/lib/db-client';
import {
  DEFAULT_ENABLED_CAPABILITIES,
  parseEnabledCapabilities,
  serializeEnabledCapabilities,
} from '@/utils/agentCapabilities';
import { formatSol, parseCapabilityTags, slugify } from '@/utils/format';
import { shareAgentLink } from '@/utils/shareAgent';

const AGENT_BASE_COST_NUM = Number(AGENT_BASE_COST);
const AGENT_PER_CAPABILITY_COST_NUM = Number(AGENT_PER_CAPABILITY_COST);

const MIN_LAMPORTS = 100_000; // 0.0001 SOL
const MAX_LAMPORTS = 500_000; // 0.0005 SOL
const STEP_LAMPORTS = 10_000;

const GRADIENT_PRESETS = [
  'linear-gradient(135deg, #00C805 0%, #00922e 60%, #00230c 100%)',
  'linear-gradient(135deg, #7c3aed 0%, #4c1d95 60%, #1e0a3c 100%)',
  'linear-gradient(135deg, #06b6d4 0%, #0e7490 60%, #082f36 100%)',
  'linear-gradient(135deg, #f97316 0%, #b45309 60%, #3a1c02 100%)',
  'linear-gradient(135deg, #ec4899 0%, #831843 60%, #2a0714 100%)',
  'linear-gradient(135deg, #eab308 0%, #713f12 60%, #241300 100%)',
];

const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
const MAX_AVATAR_BYTES = 5_000_000;

export const CreateAgentPage: React.FC = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const { agentId: editAgentId } = useParams<{ agentId?: string }>();
  const isEditMode = !!editAgentId;

  const { data: existingAgent, loading: loadingExisting } = useRealtimeData<AgentsResponse | null>(
    subscribeAgents,
    isEditMode,
    editAgentId ?? ''
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [knowledgeDoc, setKnowledgeDoc] = useState('');
  const [priceLamports, setPriceLamports] = useState(200_000);
  const [gradient, setGradient] = useState(GRADIENT_PRESETS[1]);
  const [submitting, setSubmitting] = useState(false);
  const [enabledCapabilities, setEnabledCapabilities] = useState<Set<string>>(
    new Set(DEFAULT_ENABLED_CAPABILITIES)
  );
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);

  const { balance: creditBalance, loading: creditsLoading, isAdmin } = useCreditBalance(user?.address);
  const capabilityCount = enabledCapabilities.size;
  const agentCost = AGENT_BASE_COST_NUM + AGENT_PER_CAPABILITY_COST_NUM * capabilityCount;
  const insufficientCredits =
    !isEditMode && !isAdmin && !creditsLoading && creditBalance < agentCost;

  // Profile picture state — avatarPreview is a local blob URL shown instantly on
  // selection; avatarUrl is the final public URL returned by appFiles once the
  // background upload finishes (or the existing agent's avatarUrl in edit mode).
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const [hydrated, setHydrated] = useState(false);

  // Hydrate the form once with the existing agent's data when editing.
  useEffect(() => {
    if (!isEditMode || hydrated || !existingAgent) return;
    setName(existingAgent.name ?? '');
    setDescription(existingAgent.description ?? '');
    setTags(parseCapabilityTags(existingAgent.capabilityTags));
    setSystemPrompt(existingAgent.systemPrompt ?? '');
    setKnowledgeDoc(existingAgent.knowledgeDoc ?? '');
    setPriceLamports(existingAgent.pricePerCall ?? 200_000);
    setGradient(existingAgent.avatarGradient || GRADIENT_PRESETS[1]);
    setAvatarUrl(existingAgent.avatarUrl ?? '');
    setEnabledCapabilities(parseEnabledCapabilities(existingAgent.enabledCapabilities));
    setHydrated(true);
  }, [isEditMode, hydrated, existingAgent]);

  const notAuthorized =
    isEditMode && !!existingAgent && !!user && existingAgent.creatorWallet !== user.address;

  const addTag = () => {
    const cleaned = tagInput.trim().replace(/,/g, '');
    if (!cleaned) return;
    if (!tags.includes(cleaned)) setTags([...tags, cleaned]);
    setTagInput('');
  };

  const removeTag = (tag: string) => setTags(tags.filter((t) => t !== tag));

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const handleAvatarSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      toast.error('Please choose a PNG, JPG, GIF, or WEBP image.');
      if (avatarInputRef.current) avatarInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      toast.error('That image is too large (max 5MB).');
      if (avatarInputRef.current) avatarInputRef.current.value = '';
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setAvatarPreview(objectUrl);
    setUploadingAvatar(true);
    try {
      const fileId = `agent-avatar-${crypto.randomUUID()}`;
      const uploaded = await uploadAppFiles(fileId, file);
      if (!uploaded) {
        toast.error('Could not upload image. Please try again.');
        setAvatarPreview(null);
        return;
      }
      const fileItem = await getAppFiles(fileId);
      if (!fileItem?.url) {
        toast.error('Upload succeeded but the image URL could not be retrieved.');
        setAvatarPreview(null);
        return;
      }
      setAvatarUrl(fileItem.url);
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong uploading your image.');
      setAvatarPreview(null);
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleRemoveAvatar = () => {
    setAvatarUrl('');
    setAvatarPreview(null);
  };

  const displayAvatar = avatarPreview ?? avatarUrl;

  const canSubmit =
    name.trim().length > 0 &&
    description.trim().length > 0 &&
    systemPrompt.trim().length > 10 &&
    !insufficientCredits;

  const handleSubmit = async () => {
    if (!name.trim() || !description.trim() || systemPrompt.trim().length <= 10) {
      toast.error('Name, description, and a system prompt (10+ characters) are required.');
      return;
    }
    if (!user) {
      try {
        await login();
      } catch {
        toast.error('Login failed');
      }
      return;
    }
    if (uploadingAvatar) {
      toast.error('Please wait for the photo to finish uploading.');
      return;
    }
    if (insufficientCredits) {
      toast.error(`You need ${agentCost} credits but only have ${creditBalance}. Buy more credits to continue.`);
      return;
    }

    setSubmitting(true);
    try {
      if (isEditMode && editAgentId) {
        const success = await updateAgents(editAgentId, {
          name: name.trim(),
          description: description.trim(),
          capabilityTags: tags.join(', '),
          avatarUrl: avatarUrl || '',
          avatarGradient: gradient,
          pricePerCall: priceLamports,
          systemPrompt: systemPrompt.trim(),
          knowledgeDoc: knowledgeDoc.trim() || undefined,
          enabledCapabilities: serializeEnabledCapabilities(enabledCapabilities),
          enabledCapabilitiesCount: capabilityCount,
        });

        if (!success) {
          toast.error('Could not save changes. Please try again.');
          return;
        }

        toast.success('Agent updated.');
        navigate(`/agents/${editAgentId}`);
        return;
      }

      const agentId = `${slugify(name)}-${crypto.randomUUID().slice(0, 8)}`;

      // Non-admins must pass the credit-deduction gate FIRST, using the same
      // doc id the agent will get, before the agent doc itself can be created.
      if (!isAdmin) {
        const gateOk = await setAgentCreations(agentId, {
          creator: Address.publicKey(user.address),
          capabilityCount,
          cost: agentCost,
        });
        if (!gateOk) {
          toast.error('Could not authorize this agent — you may not have enough credits.', {
            action: { label: 'Buy credits', onClick: () => setBuyCreditsOpen(true) },
          });
          return;
        }
      }

      const success = await setAgents(agentId, {
        creatorWallet: Address.publicKey(user.address),
        name: name.trim(),
        description: description.trim(),
        capabilityTags: tags.join(', '),
        avatarUrl: avatarUrl || '',
        avatarGradient: gradient,
        isFunctional: true,
        pricePerCall: priceLamports,
        acceptedToken: 'SOL',
        creatorSplitPct: 80,
        platformSplitPct: 20,
        statsRuns: 0,
        statsRating: 0,
        statsVolume: 0,
        createdAt: Time.Now,
        systemPrompt: systemPrompt.trim(),
        knowledgeDoc: knowledgeDoc.trim() || undefined,
        enabledCapabilities: serializeEnabledCapabilities(enabledCapabilities),
        enabledCapabilitiesCount: capabilityCount,
      });

      if (!success) {
        toast.error('Could not publish your agent. Please try again.');
        return;
      }

      toast.success('Agent published! It now runs on Tensor Hive.', {
        action: {
          label: 'Copy share link',
          onClick: () => shareAgentLink(agentId, name.trim()),
        },
      });
      navigate(`/agents/${agentId}`);
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <PageLayout background="" fullBleed>
        <TopNav />
        <div className="container pb-24 lg:pb-10 flex flex-col items-center text-center py-24 gap-4">
          <Wallet className="w-10 h-10 text-muted-foreground" />
          <h1 className="text-xl font-bold">Connect your wallet</h1>
          <p className="text-muted-foreground max-w-sm">
            {isEditMode ? 'Sign in to edit your agent.' : 'Sign in to build and publish a no-code agent.'}
          </p>
          <button
            onClick={() => login()}
            className="rounded-full bg-[#00C805] text-white font-bold px-6 py-3 hover:bg-[#00b004] transition-colors"
          >
            Connect wallet
          </button>
        </div>
      </PageLayout>
    );
  }

  if (isEditMode && loadingExisting) {
    return (
      <PageLayout background="" fullBleed>
        <TopNav />
        <div className="container pb-24 lg:pb-10 py-24 text-center text-muted-foreground">Loading agent…</div>
      </PageLayout>
    );
  }

  if (isEditMode && !loadingExisting && !existingAgent) {
    return (
      <PageLayout background="" fullBleed>
        <TopNav />
        <div className="container pb-24 lg:pb-10 flex flex-col items-center text-center py-24 gap-4">
          <h1 className="text-xl font-bold">Agent not found</h1>
          <button onClick={() => navigate('/dashboard')} className="text-primary font-semibold">
            Back to Dashboard
          </button>
        </div>
      </PageLayout>
    );
  }

  if (notAuthorized) {
    return (
      <PageLayout background="" fullBleed>
        <TopNav />
        <div className="container pb-24 lg:pb-10 flex flex-col items-center text-center py-24 gap-4">
          <h1 className="text-xl font-bold">Not your agent</h1>
          <p className="text-muted-foreground max-w-sm">You can only edit agents you created.</p>
          <button onClick={() => navigate('/dashboard')} className="text-primary font-semibold">
            Back to Dashboard
          </button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout background="" fullBleed footer={false}>
      <TopNav />
      <div className="container pb-24 lg:pb-10 max-w-2xl">
        <PageHeader
          title={isEditMode ? 'Edit Agent' : 'Create Agent'}
          description={
            isEditMode
              ? 'Update your listing — name, description, tags, photo, price, and behavior.'
              : 'No-code builder — define a system prompt, set a price, and Tensor Hive hosts execution on our LLM. Your agent earns SOL every time someone hires it.'
          }
        />

        <div className="space-y-6 animate-fade-in-up">
          {/* Profile picture upload + gradient fallback picker */}
          <Field label="Profile picture" hint="Shown on the marketplace, profile, and in chat. PNG/JPG/GIF/WEBP, max 5MB.">
            <div className="flex items-start gap-4">
              <div className="relative w-16 h-16 rounded-2xl shrink-0 overflow-hidden shadow-inner">
                {displayAvatar ? (
                  <img src={displayAvatar} alt="Agent avatar preview" className="w-full h-full object-cover" />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center font-bold text-white text-2xl"
                    style={{ background: gradient }}
                  >
                    {name.charAt(0).toUpperCase() || '?'}
                  </div>
                )}
                {uploadingAvatar && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 text-white animate-spin" />
                  </div>
                )}
              </div>

              <div className="flex-1 flex flex-col gap-2.5">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="text-xs font-semibold rounded-full border border-input px-3 py-1.5 hover:bg-secondary transition-colors disabled:opacity-50"
                  >
                    {displayAvatar ? 'Change photo' : 'Upload photo'}
                  </button>
                  {displayAvatar && (
                    <button
                      type="button"
                      onClick={handleRemoveAvatar}
                      disabled={uploadingAvatar}
                      className="text-xs font-semibold rounded-full border border-input px-3 py-1.5 hover:bg-secondary transition-colors disabled:opacity-50"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  className="hidden"
                  onChange={handleAvatarSelect}
                />
                {!displayAvatar && (
                  <div>
                    <p className="text-[11px] text-muted-foreground mb-1.5">Or pick a gradient fallback:</p>
                    <div className="flex flex-wrap gap-2">
                      {GRADIENT_PRESETS.map((g) => (
                        <button
                          key={g}
                          type="button"
                          onClick={() => setGradient(g)}
                          aria-label="Pick gradient"
                          className={`w-7 h-7 rounded-full transition-transform duration-200 ${
                            gradient === g
                              ? 'ring-2 ring-primary ring-offset-2 ring-offset-background scale-110'
                              : 'hover:scale-110'
                          }`}
                          style={{ background: g }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Field>

          <Field label="Name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Contract Reviewer"
              className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </Field>

          <Field label="Short description">
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="One line explaining what this agent does"
              className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </Field>

          <Field label="Capability tags">
            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map((tag) => (
                <span key={tag} className="inline-flex items-center gap-1 text-xs font-semibold bg-accent text-accent-foreground px-2.5 py-1 rounded-full">
                  {tag}
                  <button onClick={() => removeTag(tag)} aria-label={`Remove ${tag}`}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
              onBlur={addTag}
              placeholder="Type a tag and press Enter (e.g. legal, writing)"
              className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </Field>

          <Field label="System prompt" hint="Defines exactly how your agent behaves for every call.">
            <Textarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              placeholder="You are an expert... Respond with plain, readable text only."
              className="min-h-[140px]"
            />
          </Field>

          <Field label="Knowledge doc (optional)" hint="Extra context/reference material your agent should use.">
            <Textarea
              value={knowledgeDoc}
              onChange={(e) => setKnowledgeDoc(e.target.value)}
              placeholder="Paste reference material, FAQs, or domain knowledge here…"
              className="min-h-[100px]"
            />
          </Field>

          <Field
            label="Capabilities"
            hint="Choose what this agent is allowed to do. Text Chat is always on. Capabilities greyed out below aren't available in this environment yet — they can't be selected."
          >
            <AgentCapabilityPicker selected={enabledCapabilities} onChange={setEnabledCapabilities} />
          </Field>

          {!isEditMode && (
            <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold flex items-center gap-1.5">
                  <Coins className="w-4 h-4 text-primary" /> This agent will cost
                </span>
                <span className="font-extrabold text-primary">
                  {isAdmin ? 'Free — Admin' : `${agentCost} credits`}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {isAdmin
                  ? 'Admin — free & unlimited agent creation.'
                  : `Base ${AGENT_BASE_COST_NUM} + ${AGENT_PER_CAPABILITY_COST_NUM} per enabled capability × ${capabilityCount}.`}
              </p>
              {!isAdmin && (
                <div className="flex items-center justify-between text-xs pt-2 border-t border-border">
                  <span className="text-muted-foreground">
                    Your balance: <span className="font-bold text-foreground">{creditsLoading ? '—' : creditBalance} credits</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setBuyCreditsOpen(true)}
                    className="font-semibold text-primary hover:underline"
                  >
                    Buy credits
                  </button>
                </div>
              )}
              {insufficientCredits && (
                <div className="rounded-xl bg-destructive/10 border border-destructive/30 text-destructive text-xs font-semibold p-2.5">
                  Not enough credits — you need {agentCost - creditBalance} more.{' '}
                  <button type="button" onClick={() => setBuyCreditsOpen(true)} className="underline">
                    Buy credits
                  </button>{' '}
                  to continue.
                </div>
              )}
            </div>
          )}

          <Field label="Price per call">
            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-muted-foreground">Renters pay this per call</span>
                <span className="font-extrabold text-primary">{formatSol(priceLamports)}</span>
              </div>
              <Slider
                min={MIN_LAMPORTS}
                max={MAX_LAMPORTS}
                step={STEP_LAMPORTS}
                value={[priceLamports]}
                onValueChange={([v]) => setPriceLamports(v)}
              />
              <div className="flex items-center justify-between mt-2 text-[11px] text-muted-foreground">
                <span>0.0001 SOL</span>
                <span>0.0005 SOL</span>
              </div>
            </div>
          </Field>

          <div className="rounded-2xl bg-accent/60 border border-border p-4 text-sm text-muted-foreground flex gap-2">
            <Sparkles className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            80% of every call goes to you, 20% to the platform — settled instantly onchain in SOL.
          </div>

          <button
            onClick={handleSubmit}
            disabled={submitting || !canSubmit || uploadingAvatar}
            className="w-full rounded-full bg-[#00C805] text-white font-bold py-4 shadow-lg shadow-[#00C805]/25 hover:bg-[#00b004] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? (isEditMode ? 'Saving…' : 'Publishing…') : isEditMode ? 'Save changes' : 'Publish agent'}
          </button>
        </div>
      </div>
      {user && <BuyCreditsDialog walletAddress={user.address} open={buyCreditsOpen} onOpenChange={setBuyCreditsOpen} />}
    </PageLayout>
  );
};

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-bold mb-1.5">{label}</label>
    {hint && <p className="text-xs text-muted-foreground mb-2">{hint}</p>}
    {children}
  </div>
);

export default CreateAgentPage;
